import React, { useMemo, useState } from 'react';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import type { Filter, FilterDataMultiSelect, FiltersGroup } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import { ShowMoreButton } from '@/app/components/search/components/ShowMoreContainer/styled.ts';

interface ShowMoreControlProps {
  hasLimit: boolean;
  limit?: number;
  onShowMore?: () => void;
  children: (filtersGroup: FiltersGroup) => React.ReactNode;
  filtersGroup: FiltersGroup;
}

export const getSlicedFiltersGroup = (filtersGroup: FiltersGroup, limit: number): FiltersGroup => {
  let remaining = limit;
  const slicedFilters = filtersGroup.filters.reduce<Filter[]>((acc, filter) => {
    if (remaining <= 0) return acc;

    const data = filter.data as FilterDataMultiSelect;
    if (filter.type === FILTER_TYPE.MULTI_SELECT && data?.options) {
      const slicedOptions = data.options.slice(0, remaining);
      remaining -= slicedOptions.length;
      acc.push({ ...filter, data: { ...data, options: slicedOptions } });
    } else {
      // Non-multi-select filters (switches, ranges, etc.) are always included
      acc.push(filter);
    }

    return acc;
  }, []);

  return { ...filtersGroup, filters: slicedFilters };
};

const ShowMoreContainer: React.FC<ShowMoreControlProps> = ({
  hasLimit,
  limit = 10,
  onShowMore,
  filtersGroup,
  children,
}) => {
  const { t } = useTranslation();

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const hasActiveFilter = filtersGroup.filters.some(
      (filter) =>
        filter.type === FILTER_TYPE.MULTI_SELECT &&
        (filter.data as FilterDataMultiSelect).options.some((o) => o.selected)
    );
    const totalOptions = filtersGroup.filters.reduce(
      (total, filter) => total + ((filter.data as FilterDataMultiSelect)?.options?.length ?? 0),
      0
    );
    return !!filtersGroup.showMore && hasLimit && !hasActiveFilter && totalOptions > limit;
  });

  const visibleFiltersGroup = useMemo(
    () => (isCollapsed ? getSlicedFiltersGroup(filtersGroup, limit) : filtersGroup),
    [isCollapsed, filtersGroup, limit]
  );

  const handleShowMore = () => {
    setIsCollapsed(false);
    onShowMore?.();
  };

  return (
    <>
      {children(visibleFiltersGroup)}
      {isCollapsed && <ShowMoreButton onClick={handleShowMore}>{t('vacation.oss.cards.view_more')}</ShowMoreButton>}
    </>
  );
};

export default ShowMoreContainer;
